import { uniqBy } from 'lodash';
import { useBoolean } from '@wegroup/design-system';
import React, { createContext, PropsWithChildren, useState } from 'react';
import { Option } from '../../../../../../../../design-system/src/content/components/SearchPickerInput';
import { InsurancePolicy } from '../../../../../../types/AdvisoryReport';
import { useTranslation } from 'react-i18next';

interface InsuranceModalContext {
  riskObjects: Option[];
  customRiskObjects: Option[];
  insuranceCompanies: Option[];
  setRiskObjects: (options: Option[]) => void;
  setInsuranceCompanies: (options: Option[]) => void;
  isCoverageEditOpen: boolean;
  setIsCoverageEditOpen: {
    on: () => void;
    off: () => void;
    toggle: () => void;
  };
  isNewCoverageDefaultOpen: boolean;
  setIsNewCoverageDefaultOpen: {
    on: () => void;
    off: () => void;
    toggle: () => void;
  };
}

export const AddInsuranceModalContext = createContext<InsuranceModalContext>({
  riskObjects: [],
  customRiskObjects: [],
  insuranceCompanies: [],
  setRiskObjects: () => null,
  setInsuranceCompanies: () => null,
  isCoverageEditOpen: false,
  setIsCoverageEditOpen: {
    on: () => null,
    off: () => null,
    toggle: () => null,
  },
  isNewCoverageDefaultOpen: false,
  setIsNewCoverageDefaultOpen: {
    on: () => null,
    off: () => null,
    toggle: () => null,
  },
});

interface Props {
  initialData?: Partial<InsurancePolicy>;
  customRiskObjects?: Option[];
  defaultIsOpenCoverage?: boolean;
}

const AddInsuranceModalContextProvider: React.FC<PropsWithChildren & Props> = ({
  children,
  initialData,
  customRiskObjects = [],
  defaultIsOpenCoverage,
}) => {
  const { t } = useTranslation();
  const [isNewCoverageDefaultOpen, setIsNewCoverageDefaultOpen] = useBoolean(
    defaultIsOpenCoverage,
  );
  const [isCoverageEditOpen, setIsCoverageEditOpen] = useBoolean(
    defaultIsOpenCoverage,
  );
  const fields = initialData?.coverages || [];

  const getDefaultRiskObjects = () => {
    return uniqBy(
      fields.flatMap(({ risk_object_id, risk_object }) => {
        /** When the coverage has no risk assigned (CMS imports) */
        if (!risk_object && !risk_object_id) {
          return { label: t('OTHER'), value: 'OTHER' };
        }
        if (risk_object && !risk_object_id) {
          return { label: risk_object, value: risk_object };
        }
        return risk_object_id && risk_object
          ? { label: risk_object, value: risk_object_id }
          : [];
      }),
      'value',
    );
  };
  const getDefaultInsuranceCompanies = () => {
    return uniqBy(
      fields.flatMap(({ insurance_company }) =>
        insurance_company
          ? { label: insurance_company, value: insurance_company }
          : [],
      ),
      'value',
    );
  };
  const [riskObjects, setRiskObjects] = useState<Option[]>(
    getDefaultRiskObjects(),
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<Option[]>(
    getDefaultInsuranceCompanies(),
  );

  const providerValue = {
    riskObjects,
    setRiskObjects,
    insuranceCompanies,
    isNewCoverageDefaultOpen,
    setIsNewCoverageDefaultOpen,
    setInsuranceCompanies,
    isCoverageEditOpen,
    customRiskObjects,
    setIsCoverageEditOpen,
  };

  return (
    <AddInsuranceModalContext.Provider value={providerValue}>
      {children}
    </AddInsuranceModalContext.Provider>
  );
};

export default AddInsuranceModalContextProvider;
